/* eslint-disable complexity */
/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { formatAddress, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { createDateRange, DateRangeInputFormik } from '@strata/react-date-time';
import { DollarAmount } from '@strata/react-dollar-amount';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { ContactInfoDialog } from 'components/shared/dialogs/ContactInfoDialog';
import { ClaimSubmissionFormAccordion } from 'components/wonReferrals/claimSubmission/ClaimSubmissionFormAccordion';
import { ClaimEstimateDialog } from 'components/wonReferrals/claimSubmission/dialogs/ClaimEstimateDialog';
import { useReferralDetails, useSelectedReferral, useSupplierDetails } from 'services/APIHooks';
import { handleError } from 'services/Error';
import { PayerFeatures, usePayerFeatures } from 'services/FeatureFlags';
import {
    ClaimFrequencyTypes,
    ClaimPlaceOfServiceMap,
    ClaimSubmissionFormSchema,
    ClaimSubmissionTypes,
    ClaimUomMap,
    ProviderAcceptAssignmentMap,
    ReleaseOfInformationMap,
} from 'services/MedicalBillingConstants';
import { AppRoutes, useCurrentReferralLocation } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { ClaimDetails, useGetClaimDetailsQuery } from 'store/api/medbillingApi';
import {
    ProductCodeOption,
    useGetDiagnosesCodesQuery,
    useGetDiagnosisQuery,
    useGetProductCodeMapQuery,
} from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';
import { submitClaim } from 'store/claims/claimDetailsThunks';

/** Regex to allow 0-4 comma separated two character codes */
const MODIFIER_CODE_REGEX = /^[\dA-z]{2}(?:,[\dA-z]{2}){0,3}$/;

/** Alpha prefixes of HMK plans not supported by FMH */
const INVALID_ALPHA_PREFIX_REGEX = /^(?!C4K|FYO|FZO|HRT|HRF|QLS|QMV|QJS|QKS).+$/;

const getInitialValueForSelect = (value = '', map: Record<string, string> = {}) =>
    map[value] && { name: map[value], secondaryData: value };

// Pre-populate service with previous NDC if present. Handles additional services with NDC not in productCodeDropdownOptions.
const getPreviouslySelectedNdc = (
    productCodeDropdownOptions: ProductCodeOption[],
    ndc: string | undefined,
    index: number,
) => {
    let selectedOption = productCodeDropdownOptions.find((code) => code.code === ndc);
    if (index > 0 && ndc) {
        selectedOption = { name: ndc, code: ndc } as ProductCodeOption;
    }
    return selectedOption;
};

const validateProcedurePrimaryJOrQCode: Yup.TestFunction<
    | {
          code?: string | undefined;
      }
    | number
    | undefined,
    Yup.AnyObject
> = (currentVal, { options, parent, path, createError }) => {
    const procedureCode = (parent as ClaimSubmissionFormSchema['services'][0]).procedure?.code;
    if (
        (options as { index: number }).index === 0 ||
        procedureCode?.startsWith('J') ||
        procedureCode?.startsWith('Q')
    ) {
        return currentVal
            ? true
            : createError({
                  path,
                  message: validationStrings.requiredField.errorMessage,
              });
    }
    return true;
};

export const ClaimSubmissionForm = () => {
    const { referralId } = useParams() as { referralId: string };
    const navigate = useNavigate();
    const location = useLocation() as Location<{
        previousEstimate?: ClaimDetails;
        isRevision?: boolean;
    }>;
    const dispatch = useAppDispatch();

    const selectedReferral = useSelectedReferral({ referralId });

    // navigate back to referrals list if referralId is invalid
    useCurrentReferralLocation(AppRoutes.wonReferrals);

    const payerId = selectedReferral?.payerId;
    const payerFeatures = usePayerFeatures(payerId);
    useEffect(() => {
        // navigate back to referral details if payer does not have MEDBILLING capability
        if (payerFeatures && !payerFeatures[PayerFeatures.MEDBILLING]) {
            navigate(AppRoutes.wonReferralDetails(referralId), { replace: true });
        }
    }, [selectedReferral, payerFeatures, referralId, navigate]);

    const referralDetails = useReferralDetails(referralId, payerId);
    const { data: memberDetails, isFetching: isMemberDetailsFetching } = useGetMemberDetailsQuery(
        referralDetails?.memberId &&
            referralDetails.memberFirstName &&
            referralDetails.memberLastName
            ? {
                  segmentId: referralDetails.segmentId,
                  memberId: referralDetails.memberId,
                  firstName: referralDetails.memberFirstName,
                  lastName: referralDetails.memberLastName,
                  dateOfBirth: referralDetails.memberDOB,
                  payerId: referralDetails.payerId,
              }
            : skipToken,
    );
    const parentSupplierDetails = useSupplierDetails(referralDetails?.supplierNpi);
    const { data: diagnosesList } = useGetDiagnosesCodesQuery();
    const { data: diagnosisData } = useGetDiagnosisQuery(
        referralDetails?.diagnosisOid ? { diagnosisOid: referralDetails.diagnosisOid } : skipToken,
    );

    const { data: productCodeMap, isFetching: productCodeMapFetching } =
        useGetProductCodeMapQuery();
    const productCodeDropdownOptions = useMemo(
        () => Object.values(productCodeMap ?? {}),
        [productCodeMap],
    );

    const [contactInfoDialogOpen, setContactInfoDialogOpen] = useState(false);

    const { previousEstimate, isRevision } = location.state ?? {};
    const [currentEstimateId, setCurrentEstimateId] = useState<string>();
    const selectedEstimate = useAppSelector((state) =>
        state.claimsList.claimsData.find((claim) => claim.transactionId === currentEstimateId),
    );
    const { data } = useGetClaimDetailsQuery(
        currentEstimateId && payerId ? { transactionId: currentEstimateId, payerId } : skipToken,
    );
    // After clicking submit estimate, must wait for transaction id to be returned from socket before loading estimate details
    const currentEstimate = currentEstimateId && selectedEstimate ? data : undefined;

    return (
        <>
            <FullscreenLoading
                isShowing={!(payerFeatures ?? referralDetails)}
                loadingText="Loading referral information..."
            />
            <Formik
                initialValues={
                    {
                        originalClaimNumber: previousEstimate?.originalClaimNumber,
                        supplierNpi:
                            previousEstimate?.supplierNpi ??
                            parentSupplierDetails?.billingSupplierNpi,
                        taxonomyCode: previousEstimate?.taxonomyCode,
                        placeOfService: getInitialValueForSelect(
                            previousEstimate?.placeOfService,
                            ClaimPlaceOfServiceMap,
                        ),
                        memberId:
                            previousEstimate?.memberId ??
                            `${memberDetails?.alphaPrefix ?? ''}${referralDetails?.memberId}`,
                        patientAccountNumber: previousEstimate?.patientAccountNumber,
                        providerSignatureOnFile: previousEstimate?.providerSignatureOnFile,
                        providerAcceptAssignment: getInitialValueForSelect(
                            previousEstimate?.providerAcceptAssignment ?? 'A',
                            ProviderAcceptAssignmentMap,
                        ),
                        assignmentOfBenefits: previousEstimate?.assignmentOfBenefits,
                        releaseOfInformation: getInitialValueForSelect(
                            previousEstimate?.releaseOfInformation,
                            ReleaseOfInformationMap,
                        ),
                        primaryDiagnosis: diagnosesList?.find(
                            (diagnosis) =>
                                diagnosis.code ===
                                (previousEstimate?.diagnoses?.[0] ??
                                    referralDetails?.diagnosisCode),
                        ),
                        additionalDiagnoses:
                            diagnosesList?.filter((diagnosis) =>
                                previousEstimate?.diagnoses?.slice(1).includes(diagnosis.code),
                            ) ?? [],
                        services: previousEstimate?.services?.map((service, index) => ({
                            key: `SERVICE${index}`,
                            dateOfService: createDateRange(
                                service.dateOfService,
                                service.dateOfServiceEnd,
                            ),
                            procedure: productCodeDropdownOptions.find(
                                (code) => code.code === service.procedureCode,
                            ),
                            modifierCodes: service.modifierCodes?.join(','),
                            ndc: getPreviouslySelectedNdc(
                                productCodeDropdownOptions,
                                service.ndc,
                                index,
                            ),
                            diagnoses:
                                diagnosesList?.filter((diagnosis) =>
                                    service.diagnoses?.includes(diagnosis.code),
                                ) ?? [],
                            billingQty: service.billingQty,
                            billingQtyUom: service.billingQtyUom,
                            dispensedQty: service.dispensedQty,
                            dispensedQtyUom: getInitialValueForSelect(
                                service.dispensedQtyUom,
                                ClaimUomMap,
                            ),
                            providerCharge: service.providerCharge,
                        })) ?? [
                            {
                                key: 'SERVICE1',
                                diagnoses:
                                    diagnosesList?.filter(
                                        (diagnosis) =>
                                            diagnosis.code === referralDetails?.diagnosisCode,
                                    ) ?? [],
                            },
                        ],
                    } as ClaimSubmissionFormSchema
                }
                initialTouched={{ memberId: true }}
                enableReinitialize
                validateOnMount
                validationSchema={Yup.object({
                    originalClaimNumber: isRevision
                        ? Yup.string().required(validationStrings.requiredField.errorMessage)
                        : Yup.string(),
                    supplierNpi: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    taxonomyCode: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    placeOfService: Yup.object().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    memberId: Yup.string()
                        .required(validationStrings.requiredField.errorMessage)
                        .max(50, validationStrings.maxCharacter[50])
                        .matches(INVALID_ALPHA_PREFIX_REGEX, {
                            message: (
                                <div className="sds_claim-submission__incompatible-plan">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.ERROR}
                                        wrapText
                                        title="INCOMPATIBLE PLAN"
                                        label="This payer plan is currently not supported. Please process this claim through your standard vendor to avoid payment delays."
                                    />
                                </div>
                            ),
                        }),
                    patientAccountNumber: Yup.string()
                        .required(validationStrings.requiredField.errorMessage)
                        .max(50, validationStrings.maxCharacter[50]),
                    providerSignatureOnFile: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    providerAcceptAssignment: Yup.object().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    assignmentOfBenefits: Yup.string().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    releaseOfInformation: Yup.object().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    primaryDiagnosis: Yup.object().required(
                        validationStrings.requiredField.errorMessage,
                    ),
                    additionalDiagnoses: Yup.array().max(11, '11 additional diagnoses max.'),
                    services: Yup.array(
                        Yup.object({
                            dateOfService: DateRangeInputFormik.validator(
                                undefined,
                                dayjs().endOf('day').toDate(),
                            ).required(validationStrings.requiredField.errorMessage),
                            procedure: Yup.object().required(
                                validationStrings.requiredField.errorMessage,
                            ),
                            modifierCodes: Yup.string().matches(
                                MODIFIER_CODE_REGEX,
                                'Must be up to 4 comma separated two digit codes.',
                            ),
                            ndc: Yup.object({ code: Yup.string() })
                                // NDC required for primary service or if  procedure is JCode
                                .test({ test: validateProcedurePrimaryJOrQCode })
                                // NDC must have length of 11
                                .test({
                                    test: (currentVal, { path, createError }) => {
                                        if (currentVal?.code?.length) {
                                            return currentVal.code.length === 11
                                                ? true
                                                : createError({
                                                      path,
                                                      message: 'NDC must have length of 11.',
                                                  });
                                        }
                                        return true;
                                    },
                                }),
                            diagnoses: Yup.array()
                                .required(validationStrings.requiredField.errorMessage)
                                .min(1, validationStrings.requiredField.errorMessage)
                                .max(4, '4 additional diagnoses max.'),
                            billingQty: Yup.number()
                                .positive(validationStrings.number.positiveNumbersOnly)
                                .required(validationStrings.requiredField.errorMessage),
                            dispensedQty: Yup.number()
                                .positive(validationStrings.number.positiveNumbersOnly)
                                // dispensedQty required for primary service or if procedure is JCode
                                .test({ test: validateProcedurePrimaryJOrQCode }),
                            dispensedQtyUom: Yup.object()
                                // dispensedQtyUom required for primary service or if procedure is JCode
                                .test({ test: validateProcedurePrimaryJOrQCode }),
                            providerCharge: Yup.number()
                                .min(0, validationStrings.number.positiveNumbersOnly)
                                .required(validationStrings.requiredField.errorMessage),
                        }).test({
                            name: 'sameCalendarYear',
                            message:
                                'All Date(s) of Service must be contained to a single calendar year. If dates extend beyond a calendar year, please submit additional claim(s).',
                            test: (_, { parent, path, createError }) =>
                                // Map all dates of service into a set of year strings & check for more than one entry
                                new Set(
                                    ((parent as ClaimSubmissionFormSchema['services']) ?? [])
                                        .map((service) =>
                                            (service.dateOfService ?? []).map((date) =>
                                                dayjs(date).year(),
                                            ),
                                        )
                                        .flat(),
                                ).size > 1
                                    ? createError({ path: `${path}.dateOfService` })
                                    : true,
                        }),
                    ).min(1),
                })}
                onSubmit={async (values) => {
                    try {
                        if (payerId && referralDetails) {
                            const formattedClaimEstimate = {
                                submissionType: ClaimSubmissionTypes.ESTIMATE,
                                friendlyId: referralDetails.friendlyId,
                                claimId: previousEstimate?.claimId ?? uuidv4(),
                                payerId,
                                referralId,
                                originalClaimNumber: values.originalClaimNumber,
                                supplierNpi: values.supplierNpi,
                                taxonomyCode: values.taxonomyCode,
                                placeOfService: values.placeOfService.secondaryData,
                                memberId: values.memberId,
                                patientAccountNumber: values.patientAccountNumber,
                                claimFrequencyType: !isRevision
                                    ? ClaimFrequencyTypes.ORIGINAL
                                    : ClaimFrequencyTypes.RESUBMIT,
                                providerSignatureOnFile: values.providerSignatureOnFile,
                                providerAcceptAssignment:
                                    values.providerAcceptAssignment.secondaryData,
                                assignmentOfBenefits: values.assignmentOfBenefits,
                                releaseOfInformation: values.releaseOfInformation.secondaryData,
                                diagnoses: [
                                    values.primaryDiagnosis.code,
                                    ...values.additionalDiagnoses.map(
                                        (diagnosis) => diagnosis.code,
                                    ),
                                ],
                                services: values.services.map((service) => ({
                                    dateOfService: service.dateOfService[0].toISOString(),
                                    dateOfServiceEnd: service.dateOfService[1].toISOString(),
                                    procedureCode: service.procedure.code,
                                    procedureCodeDescription: service.procedure.description,
                                    modifierCodes: service.modifierCodes
                                        ? service.modifierCodes.split(',')
                                        : undefined,
                                    ndc: service.ndc?.code,
                                    diagnoses: service.diagnoses.map((diagnosis) => diagnosis.code),
                                    billingQty: service.billingQty,
                                    billingQtyUom: 'UN' as keyof typeof ClaimUomMap, // Units
                                    providerCharge: service.providerCharge,
                                    dispensedQty: service.dispensedQty ?? undefined,
                                    dispensedQtyUom: service.dispensedQtyUom?.secondaryData,
                                })),
                            };

                            const transactionId = await dispatch(
                                submitClaim(formattedClaimEstimate),
                            );
                            setCurrentEstimateId(transactionId);
                        }
                    } catch (err) {
                        handleError(
                            'An error occurred submitting estimate. Please try again.',
                            err as Error,
                        );
                    }
                }}
            >
                {(formik) => (
                    <>
                        <FullscreenLoading
                            isShowing={
                                formik.isSubmitting || (!!currentEstimateId && !currentEstimate)
                            }
                            loadingText={
                                <>
                                    Generating estimate.
                                    <br />
                                    We appreciate your patience, this process could take up to one
                                    minute.
                                </>
                            }
                        />
                        <ClaimEstimateDialog
                            referralId={referralId}
                            referralDetails={referralDetails}
                            estimateData={currentEstimate}
                            isNewEstimate
                            previousEstimate={previousEstimate}
                            isOpen={!!currentEstimate}
                            onClose={() => setCurrentEstimateId(undefined)}
                        />
                        <ContactInfoDialog
                            isOpen={contactInfoDialogOpen}
                            onClose={() => setContactInfoDialogOpen(false)}
                            memberDetails={memberDetails}
                        />
                        <FormHeaderAndContent
                            rowOneContentHeader={
                                <>
                                    <ActionButtonText
                                        label="Back to Referral"
                                        isBackNavigationButton
                                        onClick={() =>
                                            navigate(AppRoutes.wonReferralDetails(referralId))
                                        }
                                    />
                                    <div style={{ marginLeft: 'auto' }}>
                                        <ActionButtonText
                                            label="Contact Information"
                                            onClick={() => setContactInfoDialogOpen(true)}
                                        />
                                    </div>
                                </>
                            }
                            rowTwoContentHeader={
                                referralDetails &&
                                !isMemberDetailsFetching && (
                                    <>
                                        <FormTitleLabelValuePair
                                            label={formatNameLastFirst(
                                                memberDetails?.lastName,
                                                memberDetails?.firstName,
                                            )}
                                            value={formatAddress(
                                                memberDetails?.memberCity,
                                                memberDetails?.memberState,
                                                memberDetails?.memberZip,
                                            )}
                                        />
                                        <HeaderLabelValuePair
                                            label="Total Claim Amount"
                                            value={
                                                <DollarAmount
                                                    amount={formik.values.services
                                                        .reduce(
                                                            (accumulator, service) =>
                                                                accumulator.plus(
                                                                    service.providerCharge || 0,
                                                                ),
                                                            new Decimal(0),
                                                        )
                                                        .toDP(2)
                                                        .toNumber()}
                                                    large
                                                />
                                            }
                                        />
                                        <HeaderLabelValuePair
                                            label="Servicing Rate"
                                            value={
                                                <ServicingRate
                                                    servicingRate={referralDetails?.winningBid}
                                                    referentPriceType={
                                                        referralDetails?.referentPriceType
                                                    }
                                                />
                                            }
                                        />
                                        <HeaderLabelValuePair
                                            label="Primary Diagnosis"
                                            value={diagnosisData?.description}
                                        />
                                        <HeaderLabelValuePair
                                            label="Product"
                                            value={selectedReferral?.product}
                                        />
                                        <HeaderLabelValuePair
                                            label="Referral Status"
                                            value={
                                                <ReferralStatusIndicator
                                                    status={referralDetails.status}
                                                />
                                            }
                                        />
                                    </>
                                )
                            }
                            mainContent={
                                <ClaimSubmissionFormAccordion
                                    isRevision={isRevision}
                                    referralDetails={referralDetails}
                                    parentSupplierDetails={parentSupplierDetails}
                                    diagnosesList={diagnosesList}
                                    productCodeMapFetching={productCodeMapFetching}
                                    productCodeDropdownOptions={productCodeDropdownOptions}
                                />
                            }
                        />
                    </>
                )}
            </Formik>
        </>
    );
};
